"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import type { Deliverable } from "@/schema/deliverable"
import { useDocumentEditorStore } from "@/stores/use-document-editor-store"
import { useQueryClient } from "@tanstack/react-query"
import { Badge, Button, Collapsible, CollapsibleContent, CollapsibleTrigger, Input, Label } from "@ui/shad"
import { ChevronDown, Loader2, MessageSquare } from "lucide-react"
import { toast } from "sonner"

import { useDeliverable, useUpdatePlanningProfileVariables } from "@/hooks/api/deliverables"
import { useApiContext } from "@/hooks/api/use-api-context"
import { useProductChatAccess } from "@/hooks/use-product-chat-access"
import { ArtifactChatPanel, useArtifactChatThread } from "@/components/artifact-editor/artifact-chat-panel"
import { ProductChatDisabled } from "@/components/chat-threads/product-chat-gate"

// Matches the deliverable editor tools' humanized_names streamed in tool-call parts.
const UPDATE_TOOL_NAMES = ["edit file", "edit document", "redesign document"]

type AdvisorVariables = NonNullable<Deliverable["advisor_variables"]>

function isValidPercent(value: string | undefined) {
  const trimmed = value?.trim()
  if (!trimmed) return false

  const percent = Number(trimmed)
  return Number.isFinite(percent) && percent >= 0 && percent <= 100
}

export function DocumentChatPanel({ deliverableId }: { deliverableId: string }) {
  const { enabled: chatEnabled, isLoading: chatAccessLoading } = useProductChatAccess()
  const queryClient = useQueryClient()
  const getContext = useApiContext()
  const { data: deliverable } = useDeliverable(deliverableId)
  const threadId = deliverable?.editor_chat_thread_id
  const { data: thread } = useArtifactChatThread(threadId, getContext)
  const [chatOpen, setChatOpen] = useState(true)
  const [variablesOpen, setVariablesOpen] = useState(true)
  const setChatBusy = useDocumentEditorStore((state) => state.setChatBusy)

  const onArtifactUpdated = useCallback(
    () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ["deliverable", deliverableId] }),
        queryClient.invalidateQueries({ queryKey: ["deliverableDraft", deliverableId] }),
      ]).then(() => undefined),
    [queryClient, deliverableId]
  )

  const beforeSubmit = useCallback(async () => {
    const { dirty, save } = useDocumentEditorStore.getState()
    if (dirty) {
      if (!save) {
        toast.error("Save or discard your document edits before sending a message")
        return false
      }
      if (!(await save())) return false
    }
    return true
  }, [])

  if (deliverable?.status === "building") {
    return <p className="text-muted-foreground p-3 text-sm">You can edit this document once it finishes generating.</p>
  }
  if (chatAccessLoading || !deliverable || (chatEnabled && (!threadId || !thread))) {
    return (
      <div className="flex h-full min-h-0 items-center justify-center rounded-md border">
        <Loader2 className="text-muted-foreground size-5 animate-spin" />
      </div>
    )
  }

  const chat =
    chatEnabled && threadId && thread ? (
      <ArtifactChatPanel
        threadId={threadId}
        thread={thread}
        getContext={getContext}
        emptyStateTitle="Edit this document with chat"
        emptyStateDescription="Edit text directly in the document. Describe layout or content changes here, or attach a file for the editor to reference."
        placeholder="Describe a change…"
        updateToolNames={UPDATE_TOOL_NAMES}
        beforeSubmit={beforeSubmit}
        onArtifactUpdated={onArtifactUpdated}
        onBusyChange={setChatBusy}
      />
    ) : (
      <ProductChatDisabled />
    )

  const variables = deliverable.advisor_variables
  if (!variables) return chat

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 overflow-hidden">
      <Collapsible
        open={variablesOpen}
        onOpenChange={setVariablesOpen}
        className={`flex min-h-0 flex-col ${variablesOpen ? (chatOpen ? "max-h-[55%]" : "flex-1") : "shrink-0"}`}
      >
        <CollapsibleTrigger className="hover:bg-tinted flex h-9 w-full shrink-0 items-center gap-2 rounded-md px-1 text-sm font-medium">
          Variables
          {variables.fields.length > 0 && (
            <Badge variant="secondary" size="xs">
              {variables.fields.length}
            </Badge>
          )}
          <ChevronDown className={`ml-auto transition-transform ${variablesOpen ? "rotate-180" : ""}`} />
        </CollapsibleTrigger>
        <CollapsibleContent className="min-h-0 flex-1 overflow-hidden rounded-md border data-[state=open]:flex data-[state=open]:flex-col">
          <DocumentVariablesPanel deliverableId={deliverable.id} variables={variables} />
        </CollapsibleContent>
      </Collapsible>

      <Collapsible
        open={chatOpen}
        onOpenChange={setChatOpen}
        className={`flex min-h-0 flex-col ${chatOpen ? "flex-1" : "shrink-0"}`}
      >
        <CollapsibleTrigger className="hover:bg-tinted flex h-9 w-full shrink-0 items-center gap-2 rounded-md px-1 text-sm font-medium">
          <MessageSquare />
          Chat
          <ChevronDown className={`ml-auto transition-transform ${chatOpen ? "rotate-180" : ""}`} />
        </CollapsibleTrigger>
        <CollapsibleContent className="min-h-0 flex-1 overflow-hidden data-[state=open]:flex data-[state=open]:flex-col">
          <div className="min-h-0 flex-1">{chat}</div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  )
}

function DocumentVariablesPanel({ deliverableId, variables }: { deliverableId: string; variables: AdvisorVariables }) {
  const updateVariables = useUpdatePlanningProfileVariables(deliverableId)
  const dirty = useDocumentEditorStore((state) => state.dirty)
  const [values, setValues] = useState<Record<string, string>>({})
  const attemptedValues = useRef<Record<string, string>>({})
  const failedPaths = useRef<string[]>([])
  const [retryVersion, setRetryVersion] = useState(0)
  const saveVariables = updateVariables.mutate

  useEffect(() => {
    if (updateVariables.isPending) return

    const changedFields = variables.fields.filter(
      (field) => isValidPercent(values[field.path]) && attemptedValues.current[field.path] !== values[field.path].trim()
    )
    if (changedFields.length === 0) return

    const timeout = window.setTimeout(() => {
      const submitted = Object.fromEntries(changedFields.map((field) => [field.path, values[field.path].trim()]))
      attemptedValues.current = { ...attemptedValues.current, ...submitted }
      saveVariables(
        {
          planningProfileId: variables.planning_profile_id,
          changes: changedFields.map((field) => ({
            path: field.path.replace(/^planning\./, ""),
            operation: "set",
            value: Number(values[field.path]) / 100,
          })),
        },
        {
          onError: () => {
            failedPaths.current = changedFields.map((field) => field.path)
          },
          onSuccess: () => {
            failedPaths.current = []
          },
        }
      )
    }, 500)

    return () => window.clearTimeout(timeout)
  }, [retryVersion, saveVariables, updateVariables.isPending, values, variables])

  const retryFailedSave = () => {
    failedPaths.current.forEach((path) => delete attemptedValues.current[path])
    updateVariables.reset()
    setRetryVersion((current) => current + 1)
  }

  const invalid = variables.fields.some((field) => values[field.path]?.trim() && !isValidPercent(values[field.path]))

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <div className="flex items-center justify-between gap-3">
          <p className="text-muted-foreground text-xs">Values update the plan automatically.</p>
          {updateVariables.isError ? (
            <Button variant="ghost" size="xs" onClick={retryFailedSave}>
              Retry
            </Button>
          ) : updateVariables.isPending ? (
            <span className="text-muted-foreground flex shrink-0 items-center gap-1.5 text-xs">
              <Loader2 className="size-3 animate-spin" />
              Saving…
            </span>
          ) : null}
        </div>

        {variables.fields.length === 0 ? (
          <p className="text-muted-foreground mt-6 text-center text-sm">All required variables have values.</p>
        ) : (
          <div className="mt-5 space-y-4">
            {variables.fields.map((field) => (
              <div key={field.path} className="space-y-1.5">
                <Label htmlFor={`plan-variable-${field.path}`}>{field.label}</Label>
                <div className="relative">
                  <Input
                    id={`plan-variable-${field.path}`}
                    type="number"
                    min={0}
                    max={100}
                    step="0.1"
                    value={values[field.path] ?? ""}
                    onChange={(event) => setValues((current) => ({ ...current, [field.path]: event.target.value }))}
                    disabled={dirty}
                    className="pr-8"
                  />
                  <span className="text-muted-foreground pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-sm">
                    %
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {invalid && <p className="text-red-accent mt-4 text-xs">Enter percentages from 0 to 100.</p>}
      </div>
    </div>
  )
}
